import React from 'react';
import { Check, ExternalLink, QrCode, Users, Heart, Stethoscope, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const HealthCard = () => {
  const silverBenefits = [
    'Unlimited Doctor Consultations (In-Center, including Night)',
    '15% Discount in Pharmacy Bill',
    '15% Discount in All Lab Tests',
    '15% Discount in All Scans & X-Rays',
    'Night Consultations Included',
    'No appointment needed for nighttime visits',
  ];

  const goldBenefits = [
    'All Silver Plan Benefits Included',
    '3 Tele-consultations per Month (Scheduled call time only)',
    '1 Premium Master Health Checkup per Year (For 1 Person)',
    'Includes: Blood Sugar, CBC, Bilirubin, Creatinine, Total Cholesterol, Triglycerides, TSH',
    'Night Consultations Included',
  ];

  const whyChoose = [
    {
      icon: Users,
      title: 'Perfect for Families',
      description: 'Cover up to 4 family members with a single plan',
      color: 'text-blue-500',
    },
    {
      icon: Heart,
      title: 'Chronic Condition Management',
      description: 'Ideal for managing Diabetes, Hypertension and other chronic conditions',
      color: 'text-red-500',
    },
    {
      icon: Stethoscope,
      title: 'Employer Benefits',
      description: 'Invest in your staff\'s well-being with comprehensive healthcare',
      color: 'text-green-500',
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <section
        className="relative py-20 px-4"
        style={{
          background: 'linear-gradient(135deg, #0A2540 0%, #0EA5E9 100%)',
        }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
            <span className="text-white font-semibold">JOIN THE HEALTH REVOLUTION</span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="health-card-heading"
          >
            AMC Health Card
          </h1>
          <p className="text-2xl text-white/90 mb-4 font-semibold">
            Choose Your Family Protection
          </p>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Your Subscription to a Healthier Life. Fixed Monthly Fee. Full Peace of Mind.
          </p>
        </div>
      </section>

      <section className="py-8 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/90qj3n4i_1000566014.png"
            alt="AMC Health Card - First Time in Chennai - Join Health Revolution"
            className="w-full rounded-2xl shadow-2xl"
            data-testid="health-card-hero-image"
          />
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              The AMC Advantage
            </h2>
            <p className="text-[#475569] text-lg">
              Affordable, Membership, Comprehensive Healthcare for Your Entire Family
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-8 shadow-xl border-4 border-slate-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-slate-300/30 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="bg-white rounded-2xl p-4 shadow-md">
                    <h3
                      className="text-3xl font-bold text-[#0A2540]"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      SILVER PLAN
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-[#0A2540]">₹200</div>
                    <div className="text-sm text-[#475569] font-medium">PER MONTH</div>
                    <div className="text-xs text-[#475569]">(Family of 4)</div>
                  </div>
                </div>

                <div className="mb-6">
                  <img
                    src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/zfnh1jj1_WhatsApp%20Image%202026-04-19%20at%201.08.56%20PM.jpeg"
                    alt="Silver Plan Benefits"
                    className="w-full rounded-xl shadow-lg"
                  />
                </div>

                <ul className="space-y-3 mb-8">
                  {silverBenefits.map((benefit) => (
                    <li key={benefit} className="flex items-start">
                      <Check className="h-5 w-5 text-[#0EA5E9] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-[#0A2540] font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://forms.gle/Kv6yDgSkAniixb3s7"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="silver-plan-join-button"
                >
                  <Button className="w-full bg-[#0A2540] text-white hover:bg-[#0A2540]/90 rounded-full py-6 text-lg font-semibold">
                    Join Silver Plan
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-yellow-100 rounded-3xl p-8 shadow-2xl border-4 border-amber-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-yellow-300/30 rounded-full -mr-20 -mt-20"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-300/30 rounded-full -ml-16 -mb-16"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="bg-gradient-to-r from-amber-400 to-yellow-500 rounded-2xl p-4 shadow-md">
                    <h3
                      className="text-3xl font-bold text-white"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      GOLD PLAN ⭐
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-[#0A2540]">₹500</div>
                    <div className="text-sm text-[#475569] font-medium">PER MONTH</div>
                    <div className="text-xs text-[#475569]">(Family of 4)</div>
                  </div>
                </div>

                <div className="mb-6">
                  <img
                    src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/208mubdi_WhatsApp%20Image%202026-04-19%20at%201.08.57%20PM.jpeg"
                    alt="Gold Plan Benefits"
                    className="w-full rounded-xl shadow-lg"
                  />
                </div>

                <ul className="space-y-3 mb-8">
                  {goldBenefits.map((benefit) => (
                    <li key={benefit} className="flex items-start">
                      <Check className="h-5 w-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-[#0A2540] font-semibold">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://forms.gle/Kv6yDgSkAniixb3s7"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gold-plan-join-button"
                >
                  <Button className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 text-white hover:from-amber-600 hover:to-yellow-700 rounded-full py-6 text-lg font-semibold shadow-lg">
                    Join Gold Plan
                    <ExternalLink className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg mb-12">
            <h2
              className="text-3xl font-bold text-[#0A2540] mb-8 text-center"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Who is it for?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {whyChoose.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="text-center">
                    <div className="bg-[#F8FAFC] rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                      <Icon className={`h-10 w-10 ${item.color}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-[#0A2540] mb-2">{item.title}</h3>
                    <p className="text-[#475569]">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] rounded-2xl p-8 md:p-12 text-center">
            <div className="max-w-4xl mx-auto">
              <h2
                className="text-3xl font-bold text-white mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Ready to Join the Health Revolution?
              </h2>
              <p className="text-white/90 text-lg mb-8">
                Scan the QR code or click below to join now and start enjoying comprehensive healthcare benefits
              </p>

              <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-xl">
                  <div className="flex items-center justify-center mb-3">
                    <QrCode className="h-6 w-6 text-[#0A2540] mr-2" />
                    <span className="font-semibold text-[#0A2540]">Scan to Join</span>
                  </div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/71p2g0c0_WhatsApp%20Image%202026-04-11%20at%207.55.22%20PM.jpeg"
                    alt="Join AMC Health Card - QR Code"
                    className="w-48 h-48 mx-auto"
                  />
                </div>

                <div className="text-center">
                  <p className="text-white text-xl mb-4 font-semibold">OR</p>
                  <a
                    href="https://forms.gle/Kv6yDgSkAniixb3s7"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="health-card-cta-button"
                  >
                    <Button className="bg-white text-[#0A2540] hover:bg-white/90 rounded-full px-12 py-6 text-xl font-bold shadow-xl">
                      Click to Join & Automate Now
                      <ExternalLink className="ml-3 h-6 w-6" />
                    </Button>
                  </a>
                  <p className="text-white/80 mt-4 text-sm">Fixed Monthly Fee. Full Peace of Mind.</p>
                </div>
              </div>

              <div className="flex items-center justify-center space-x-6 text-white">
                <div className="flex items-center">
                  <Phone className="h-5 w-5 mr-2" />
                  <a href="tel:7010200869" className="hover:underline">
                    7010200869
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="h-5 w-5 mr-2" />
                  <a href="tel:6380608090" className="hover:underline">
                    6380608090
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthCard;